/**
 * Customer 360 · the head (the owner, 2026-10-22, his own spec after the twenty designs):
 *   · one structured band — a small circle ringed in the customer's health, and beside it at the same height the name,
 *     code, status and health, then the whole mobile number (no reason asked: 139500), the address and area, the door,
 *     the round and the rider; on the right the Active switch, the quick actions and ✎;
 *   · Edit opens Overview › Personal & family: the onboarding form, filled, to edit (the owner, 2026-10-22);
 *   · eight KPIs across the full width just above the tabs: units this month, outstanding, last payment, bottles held,
 *     open tickets, what is delivered (variant × qty), the zone, and the last 30 days as a behaviour strip; and first, the
 *     credit limit (139700), with joined / with us / subscription since under the band (the owner, 2026-10-22).
 *
 * Every figure is a read the page already makes (customerReads.ts, the same query keys): nothing new is computed and a
 * read that is refused or empty leaves its figure out.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Phone, Pencil, Wallet, MessageSquareWarning, CalendarDays, FileUp } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { Button, Status, cx, plainError } from '../../ui/primitives';
import { Field, Input } from '../../ui/forms';
import { Dialog } from '../../ui/overlays';
import { toast } from '../../ui/toast';
import { date as fmtDate } from '../../ui/format';
import { VariantBottle } from '../../ui/VariantBottle';
import { ChangeDeliveryButton } from './Customer360Overview';
import { CustomerStatusSwitch } from './CustomerManage';
import { MoreMenu, useOpenTab } from './CustomerViews';
import {
  addDays, balanceSourceLine, dayLines, inr, istToday, monthStart, outstandingSplit, shortDay, useCalendarMonth, useComplaints, usePlannedDoors, useCustomerBalance, useDeliveryFacts, useMonthBill,
  type CalendarDay,
} from './customerReads';
import type { Customer360 } from './customerDescriptor';

interface Contact { phone: string | null; altPhone: string | null; whatsapp: boolean | null; consentWhatsapp: boolean; mayReveal: boolean; since: string | null }
interface Credit { owedPaise: number; avgDailyPaise: number; tier: string; limitPaise: number | null; limitSource: 'OFFICE' | 'WORKED_OUT' | null;
  limitDays: number[] | null; usedPct: number | null; daysLeft: number | null; level: 'L1' | 'L2' | 'L3' | null; mayChange: boolean }
type Level = 'GREEN' | 'AMBER' | 'RED';

/** The brief's one health legend (§1.1): the word, and the colour of the ring and the dot. */
const HEALTH: Record<Level, { word: string; ring: string; chip: string }> = {
  GREEN: { word: 'Healthy', ring: 'border-accepted', chip: 'bg-accepted-ground text-accepted' },
  AMBER: { word: 'Watch', ring: 'border-warn-mark', chip: 'bg-queued-ground text-queued' },
  RED: { word: 'At risk', ring: 'border-crit-mark', chip: 'bg-refused-ground text-refused' },
};

const PRIME = { word: 'Prime', ring: 'border-prime', chip: 'bg-info-ground text-prime' };

/** A phone number in groups the eye can read: 98 2911 4821. A masked one is shown as it comes. */
const grouped = (p: string) => { const d = p.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, ''); return /^\d{10}$/.test(d) ? `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}` : p; };
const digits = (p: string) => p.replace(/\D/g, '').replace(/^(?!91)(\d{10})$/, '91$1');
const isFull = (p: string | null | undefined): p is string => !!p && !p.includes('•') && !p.includes('*');

/** How long with us, from a date: "1 yr 7 mo", "3 mo 5 d", "12 d". */
function withUs(from: string, today: string): string {
  const a = new Date(`${from.slice(0, 10)}T00:00:00Z`); const b = new Date(`${today}T00:00:00Z`);
  if (b < a) return '—';
  let months = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + b.getUTCMonth() - a.getUTCMonth();
  if (b.getUTCDate() < a.getUTCDate()) months -= 1;
  const anchor = new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() + months, a.getUTCDate()));
  const days = Math.round((b.getTime() - anchor.getTime()) / 86_400_000);
  const y = Math.floor(months / 12); const m = months % 12;
  if (y > 0) return `${y} yr${m ? ` ${m} mo` : ''}`;
  if (m > 0) return `${m} mo${days ? ` ${days} d` : ''}`;
  return `${days} d`;
}

/**
 * The credit limit (139700; the owner, 2026-10-22: "credit limit top par show honi mandatory hai"): ₹ limit, how much of
 * it is used, the days left at the customer's own daily bill, the level's colour. Worked out (L3 days × the average daily
 * bill) unless the office set the customer's own; whoever may adjust changes it here. Reminders only — nothing pauses.
 */
function CreditKpi({ customerId, onOpen }: { customerId: string; onOpen: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['customer-credit', customerId], retry: false, queryFn: () => rpc<Credit>('customer_credit', { p_customer_id: customerId }) });
  const [editing, setEditing] = useState(false);
  const [rupees, setRupees] = useState('');
  const save = useMutation({
    mutationFn: (paise: number | null) => rpc<{ detail: string }>('set_customer_credit_limit', { p_customer_id: customerId, p_limit_paise: paise, p_reason: null }),
    onSuccess: (r) => { toast(r.detail, { tone: 'ok' }); setEditing(false); void qc.invalidateQueries({ queryKey: ['customer-credit', customerId] }); },
  });
  if (q.error) return <Kpi label="Credit limit">—</Kpi>;
  const c = q.data;
  const tone = c?.level === 'L3' ? 'text-refused' : c?.level === 'L2' ? 'text-queued' : c?.level === 'L1' ? 'text-warn-mark' : undefined;
  const note = !c ? undefined : c.limitPaise === null ? 'no bill yet to work it out'
    : [`${c.usedPct ?? 0}% used`, c.level === 'L3' ? 'limit reached' : c.daysLeft !== null ? `${c.daysLeft} d left` : null,
       c.limitSource === 'OFFICE' ? 'set by office' : null].filter(Boolean).join(' · ');
  return (
    <>
      <div className={cx('min-w-0 rounded-[var(--radius-panel)] border bg-surface px-3 py-2.5', c?.level === 'L3' ? 'border-crit-mark' : c?.level === 'L2' ? 'border-warn-mark' : 'border-line')}>
        <span className="flex items-center justify-between gap-1">
          <span className="block text-label uppercase text-ink-muted">Credit limit</span>
          {c?.mayChange ? <button type="button" className="text-small text-brand hover:underline" onClick={() => { setRupees(c.limitPaise ? String(Math.round(c.limitPaise / 100)) : ''); setEditing(true); }}>Change</button> : null}
        </span>
        <button type="button" onClick={onOpen} className="block w-full min-w-0 text-left">
          <span className={cx('mt-1 block truncate text-[1.25rem] font-semibold leading-tight tracking-tight tabular-nums', tone)}>{c ? (c.limitPaise === null ? '—' : inr(c.limitPaise)) : '…'}</span>
          {c?.limitPaise ? (
            <span aria-hidden className="mt-1 block h-1 overflow-hidden rounded-full bg-line">
              <span className={cx('block h-full', c.level === 'L3' ? 'bg-crit-mark' : c.level === 'L2' || c.level === 'L1' ? 'bg-warn-mark' : 'bg-brand')} style={{ width: `${Math.min(100, c.usedPct ?? 0)}%` }} />
            </span>) : null}
          {note ? <span className="mt-0.5 block truncate text-small text-ink-muted" title={note}>{note}</span> : null}
        </button>
      </div>
      <Dialog open={editing} onClose={() => setEditing(false)} title="Credit limit"
        footer={<div className="flex w-full flex-wrap items-center justify-between gap-2">
          {c?.limitSource === 'OFFICE' ? <Button disabled={save.isPending} onClick={() => save.mutate(null)}>Back to the worked-out limit</Button> : <span />}
          <div className="flex gap-2">
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" disabled={save.isPending || !(Number(rupees) > 0)} onClick={() => save.mutate(Math.round(Number(rupees) * 100))}>Save</Button>
          </div>
        </div>}>
        <div className="grid gap-3">
          <p className="text-small text-ink-muted">
            Worked out: {c?.limitDays ? `${c.limitDays[2]} days` : '—'} × the average daily bill {c ? inr(c.avgDailyPaise) : '—'} ({c?.tier?.toLowerCase()} customer).
            A limit set here replaces it for this customer. Reminders only: nothing is paused when the limit is reached.
          </p>
          <Field label="Limit (₹)"><Input type="number" min={1} inputMode="numeric" value={rupees} onChange={(e) => setRupees(e.target.value)} autoFocus /></Field>
          {save.error ? <p role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</p> : null}
        </div>
      </Dialog>
    </>
  );
}

/** What one address gets on a day (140900), from that address's calendar. */
function DoorDelivers({ customerId, door, label, day }: { customerId: string; door: string; label: string; day: string }) {
  const cal = useCalendarMonth(customerId, monthStart(day), true, door);
  const d = cal.data?.days.find((x) => x.date === day);
  const lines = d ? dayLines(d).filter((l) => l.quantity > 0) : null;
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 truncate">
      <span className="text-ink-muted">{label}:</span>
      {lines === null ? '…' : lines.length === 0 ? <span className="text-ink-muted">nothing</span>
        : lines.map((l) => <span key={l.variant} className="inline-flex items-center gap-0.5"><VariantBottle code={l.variant} size={13} />×{l.quantity}</span>)}
    </span>
  );
}

/** One labelled fact in the band's second line. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return <span className="inline-flex min-w-0 items-baseline gap-1.5"><span className="text-label uppercase text-ink-faint">{label}</span><span className="min-w-0 truncate text-body text-ink">{children}</span></span>;
}

/** One KPI tile: a figure, a label, an optional note; a click opens the tab that holds it. */
function Kpi({ label, children, note, tone, onClick }: { label: string; children: ReactNode; note?: ReactNode; tone?: string; onClick?: () => void }) {
  const body = (
    <>
      <span className="block text-label uppercase text-ink-muted">{label}</span>
      <span className={cx('mt-1 block min-w-0 truncate text-[1.25rem] font-semibold leading-tight tracking-tight tabular-nums', tone)}>{children}</span>
      {note ? <span className="mt-0.5 block truncate text-small text-ink-muted">{note}</span> : null}
    </>
  );
  const cls = 'min-w-0 rounded-[var(--radius-panel)] border border-line bg-surface px-3 py-2.5 text-left';
  return onClick ? <button type="button" onClick={onClick} className={cx(cls, 'hover:border-line-strong hover:bg-surface-sunken')}>{body}</button> : <div className={cls}>{body}</div>;
}

type Mark = 'given' | 'failed' | 'skipped' | 'none';
/** The same colours as the month grid: given, failed (an order the door did not get), skipped (planned, then taken off). */
function markOf(d: CalendarDay, today: string): { mark: Mark; units: number } {
  const units = dayLines(d).reduce((n, l) => n + l.quantity, 0);
  if (d.delivered && d.delivered.given > 0) return { mark: 'given', units: d.delivered.given };
  if (d.delivered?.outcome === 'NOT_DELIVERED') return { mark: 'failed', units };
  if (d.date < today && d.order && d.order.status !== 'CANCELLED' && units > 0) return { mark: 'failed', units };
  if (d.changes.length > 0 && d.planned.length > 0) return { mark: 'skipped', units: 0 };
  return { mark: 'none', units: 0 };
}

/** The last 30 days, one bar a day: its height the units given, its colour what happened. */
function Behaviour({ customerId }: { customerId: string }) {
  const today = istToday();
  const from = addDays(today, -30);
  const a = useCalendarMonth(customerId, monthStart(from));
  const b = useCalendarMonth(customerId, monthStart(today), monthStart(from) !== monthStart(today));
  const days = [...(a.data?.days ?? []), ...(monthStart(from) !== monthStart(today) ? b.data?.days ?? [] : [])]
    .filter((d) => d.date >= from && d.date < today);
  if (!a.data) return <span className="text-small text-ink-muted">…</span>;
  const marks = days.map((d) => ({ date: d.date, ...markOf(d, today) }));
  const max = Math.max(1, ...marks.map((m) => m.units));
  const given = marks.filter((m) => m.mark === 'given').length;
  const missed = marks.filter((m) => m.mark === 'failed').length;
  const skipped = marks.filter((m) => m.mark === 'skipped').length;
  return (
    <>
      <span role="img" aria-label={`Last 30 days: ${given} delivered, ${skipped} skipped, ${missed} missed`} className="mt-1 flex h-7 items-end gap-px">
        {marks.map((m) => (
          <span key={m.date} title={`${shortDay(m.date)} · ${m.mark === 'none' ? 'nothing due' : m.mark}${m.units ? ` · ${m.units}` : ''}`}
            className={cx('flex-1 rounded-t-[1px]', m.mark === 'given' ? 'bg-brand' : m.mark === 'failed' ? 'bg-crit-mark' : m.mark === 'skipped' ? 'bg-warn-mark' : 'bg-line')}
            style={{ height: m.mark === 'given' ? `${Math.max(30, (m.units / max) * 100)}%` : m.mark === 'none' ? '10%' : '45%' }} />))}
      </span>
      <span className="mt-0.5 block truncate text-small text-ink-muted">{given} given · {skipped} skipped · {missed} missed</span>
    </>
  );
}

export function CustomerProfileHead({ entity, open }: { entity: Customer360; open: (tab: string) => void }) {
  const today = istToday();
  const facts = useDeliveryFacts(entity.id);
  const balance = useCustomerBalance(entity.id);
  const bill = useMonthBill(entity.id, today.slice(0, 7));
  const complaints = useComplaints(entity.id);
  const tomorrow = addDays(today, 1);
  const cal = useCalendarMonth(entity.id, monthStart(tomorrow));
  const health = useQuery({ queryKey: ['customer-health', entity.id], queryFn: () => rpc<{ level: Level; reasons: { text: string }[] }>('customer_health', { p_customer_id: entity.id }) });
  const contact = useQuery({ queryKey: ['customer-contact', entity.id], queryFn: () => rpc<Contact>('customer_contact', { p_customer_id: entity.id }) });
  const prime = useQuery({ queryKey: ['prime-customers-rows'], staleTime: 300_000, retry: false,
    queryFn: () => rpc<{ customer_id: string; months: number }[]>('prime_customers') });
  const primeRow = prime.data?.find((r) => r.customer_id === entity.id);
  const openTab = useOpenTab();
  const doorRoutes = useQuery({ queryKey: ['door-routes', entity.id], retry: false,
    queryFn: () => rpc<{ code: string; label: string | null; ownRoute: string | null; status: string }[]>('door_routes', { p_customer_id: entity.id }) });

  // Prime (137000) sits above the nightly colour: the ring and the chip read Prime.
  const h = primeRow ? PRIME : health.data ? HEALTH[health.data.level] : null;
  const f = facts.data;
  const door = entity.locations.find((l) => l.is_primary && l.status === 'ACTIVE') ?? entity.locations.find((l) => l.status === 'ACTIVE') ?? entity.locations[0];
  const c = contact.data;
  // The whole number (the owner, 2026-10-22): customer_360 and customer_contact (139500) give it to whoever may see it.
  const phone = isFull(c?.phone) ? c.phone : isFull(entity.phone) ? entity.phone : c?.phone ?? entity.phone;
  const alt = c?.altPhone ?? null;
  const units = bill.data ? bill.data.byVariant.reduce((n, v) => n + v.units, 0) : null;
  const monthName = new Date(`${today}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  const openTickets = complaints.data ? complaints.data.filter((x) => x.status === 'RECEIVED').length : null;
  const initials = entity.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
  const next = cal.data?.days.find((d) => d.date === tomorrow);
  const nextLines = next ? dayLines(next).filter((l) => l.quantity > 0) : null;
  const plannedDoors = usePlannedDoors(entity.id);   // 140900: two addresses with a plan — tomorrow by address
  const owed = balance.data?.balance_paise ?? 0;
  // Joined: the date the office recorded (customer since, also the khata's), else the first delivery, else the record.
  const joined = c?.since ?? f?.since ?? entity.created_at ?? null;
  const subSince = (entity.subscriptions ?? []).filter((s) => s.status !== 'CANCELLED').map((s) => s.start_date).sort()[0] ?? null;

  return (
    <div className="grid gap-3 pb-1">
      <div className="flex items-start gap-4">
        {/* The face: initials in a small circle, ringed by the health; it stands as tall as the two lines beside it. */}
        <div aria-hidden className={cx('flex size-14 shrink-0 items-center justify-center rounded-full border-[3px] bg-surface-sunken text-body font-semibold text-ink-muted', h?.ring ?? 'border-line-strong')}>{initials}</div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="text-[1.375rem] font-semibold leading-tight tracking-tight text-ink">{entity.name}</h1>
            <span className="font-mono text-body text-ink-muted">{entity.code}</span>
            <Status code={entity.status} />
            {h ? <span title={h === PRIME ? 'With us 6+ months, pays on time, no pause in 90 days' : health.data?.reasons.map((r) => r.text).join(' · ') || undefined}
              className={cx('inline-flex items-center gap-1.5 rounded-[var(--radius-badge)] px-1.5 py-0.5 text-badge uppercase', h.chip)}>
              <span aria-hidden className="size-2 rounded-full bg-current" />{h.word}</span> : null}
            {entity.segment ? <span className="rounded-[var(--radius-badge)] bg-info-ground px-1.5 py-0.5 text-badge uppercase text-info">{entity.segment}</span> : null}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="w-[9.5rem]"><CustomerStatusSwitch entity={entity} /></div>
              <ChangeDeliveryButton label="Exception" variant="secondary" />
              <Button size="dense" onClick={() => open('money')}><Wallet size={13} aria-hidden /> Payment</Button>
              <Button size="dense" onClick={() => open('service')}><MessageSquareWarning size={13} aria-hidden /> Complaint</Button>
              <MoreMenu items={[
                <Button key="day" size="dense" onClick={() => open('calendar')}><CalendarDays size={13} aria-hidden /> Change a day</Button>,
                <Button key="doc" size="dense" onClick={() => open('activity')}><FileUp size={13} aria-hidden /> Add a document</Button>,
              ]} />
              <Button size="dense" onClick={() => openTab('overview', 'family')} title="Edit the details in the onboarding form: contact, family, addresses and pins, route">
                <Pencil size={13} aria-hidden /> Edit</Button>
            </div>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-[1.0625rem] font-semibold tabular-nums text-ink">{phone ? grouped(phone) : '—'}</span>
              {isFull(phone) ? <>
                <a href={`tel:+${digits(phone)}`} aria-label="Call" className="inline-flex size-7 items-center justify-center rounded-[var(--radius-control)] border border-line hover:bg-surface-sunken"><Phone size={13} aria-hidden /></a>
                {c?.whatsapp !== false ? <a href={`https://wa.me/${digits(phone)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"
                  className="inline-flex size-7 items-center justify-center rounded-[var(--radius-control)] border border-line hover:bg-surface-sunken"><MessageCircle size={13} aria-hidden /></a> : null}
              </> : null}
            </span>
            {alt ? <Fact label="Alt"><span className="font-mono tabular-nums">{grouped(alt)}</span></Fact> : null}
            <Fact label="Address">{door ? <>{door.address_line ?? door.code}{door.area ? <span className="text-ink-muted"> · {door.area}</span> : null}</> : <span className="text-queued">No door yet</span>}</Fact>
            {door ? <Fact label="Door"><span className="font-mono">{door.code}</span></Fact> : null}
            <Fact label="Route"><span className="font-mono">{entity.route?.code ?? '—'}</span></Fact>
            {/* 140500: an address on a round of its own */}
            {(doorRoutes.data ?? []).filter((x) => x.ownRoute && x.status === 'ACTIVE' && x.ownRoute !== entity.route?.code).map((x) => (
              <Fact key={x.code} label={x.label ?? x.code}><span className="font-mono">{x.ownRoute}</span></Fact>))}
            <Fact label="Rider">{f?.route?.usualRider ?? '—'}</Fact>
          </div>
          {/* The owner, 2026-10-22: when they joined, how long with us, and since when the subscription runs. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1">
            <Fact label="Joined">{joined ? fmtDate(joined) : '—'}</Fact>
            <Fact label="With us">{joined ? withUs(joined, today) : '—'}</Fact>
            <Fact label="Subscription since">{subSince ? fmtDate(subSince) : '—'}</Fact>
          </div>
        </div>

      </div>


      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-9">
        <CreditKpi customerId={entity.id} onOpen={() => open('money')} />
        <Kpi label={`Units · ${monthName}`} note="so far this month" onClick={() => open('calendar')}>{units ?? '—'}</Kpi>
        <Kpi label="Outstanding" tone={owed > 0 ? 'text-refused' : undefined} onClick={() => open('money')}
          note={balance.data ? `${outstandingSplit(balance.data)} · ${balanceSourceLine(balance.data.source)}${balance.data.delivery_days_since_paid ? ` · ${balance.data.delivery_days_since_paid} delivery days unpaid` : ''}` : undefined}>
          {balance.error ? '—' : inr(balance.data?.balance_paise)}</Kpi>
        <Kpi label="Last payment" onClick={() => open('money')} note={balance.data?.paid_paise ? `${inr(balance.data.paid_paise)} paid all time` : undefined}>
          {balance.data?.last_paid_on ? shortDay(balance.data.last_paid_on) : balance.data ? 'never' : '—'}</Kpi>
        <Kpi label="Bottles held" tone={(f?.bottlesOwed ?? 0) >= 6 ? 'text-queued' : undefined} onClick={() => open('deliveries')}
          note={f?.bottlesOwed ? `oldest out ${f.oldestOwedDays ?? 0} d` : undefined}>{f ? f.bottlesOwed ?? 0 : '—'}</Kpi>
        <Kpi label="Open tickets" tone={openTickets ? 'text-queued' : undefined} onClick={() => open('service')}>{openTickets ?? '—'}</Kpi>
        <Kpi label={`Delivers · ${shortDay(tomorrow)}`} onClick={() => open('calendar')}>
          {plannedDoors.length > 1
            ? <span className="grid gap-0.5 text-[0.875rem]">{plannedDoors.map((d) => <DoorDelivers key={d.locationId} customerId={entity.id} door={d.locationId} label={d.label ?? d.code} day={tomorrow} />)}</span>
            : nextLines === null ? '—' : nextLines.length === 0 ? <span className="text-ink-muted">nothing</span> : (
            <span className="flex flex-wrap items-center gap-x-2 text-[1rem]">
              {nextLines.map((l) => <span key={l.variant} className="inline-flex items-center gap-1"><VariantBottle code={l.variant} size={16} />×{l.quantity}</span>)}
            </span>)}
        </Kpi>
        <Kpi label="Zone" note={[entity.route?.code ? `route ${entity.route.code}` : null, entity.city?.name].filter(Boolean).join(' · ') || undefined}>
          <span className="text-[1rem]">{door?.area ?? '—'}</span></Kpi>
        <div className="min-w-0 rounded-[var(--radius-panel)] border border-line bg-surface px-3 py-2.5">
          <span className="block text-label uppercase text-ink-muted">Last 30 days</span>
          <Behaviour customerId={entity.id} />
        </div>
      </div>
    </div>
  );
}
